// A small, live instance of the real engine, embedded in a lesson chapter.
//
// This is the same LSM class the sandbox runs — only the configuration is
// tiny (a memtable that holds a handful of entries, two or three levels), so
// a learner can watch individual keys move instead of a blur of 500. Nothing
// here is scripted: the buttons call put/get/delete and the panels render
// whatever the engine actually did.

import { LSM } from "../engine/lsm";
import { MemoryStore } from "../engine/store";
import { KVStore } from "../engine/store";
import { EngineConfig, TableMeta } from "../engine/types";
import { Trace } from "../engine/trace";
import { Workload } from "../engine/workload";
import { renderTraceSteps } from "../ui/tracelist";

export type Panel = "wal" | "memtable" | "levels" | "trace" | "metrics";

export interface DemoButton {
  label: string;
  /** Optional hint shown under the button row while this action runs. */
  title?: string;
  run: (db: LSM, demo: MiniDemo) => Promise<void>;
}

export interface DemoSpec {
  /** Engine config overrides — keep these small so the demo is legible. */
  config?: Partial<EngineConfig>;
  panels: Panel[];
  buttons: DemoButton[];
  /** Entries written before the learner sees it, to set a starting state. */
  preload?: [string, string][];
  /** Caption under the demo. */
  note?: string;
  /** Show the keys stored inside each SSTable (decoded from its bytes). */
  expandTables?: boolean;
}

const TINY: Partial<EngineConfig> = {
  memtableBytes: 80,
  l0Trigger: 2,
  numLevels: 3,
  fanout: 2,
  levelBaseBytes: 400,
  sparseEvery: 2,
  bloomBitsPerKey: 10,
  tieredRunTrigger: 2,
};

interface StrategyResult {
  wa: number;
  ra: number;
  sa: number;
  files: number;
  compactions: number;
}

export class MiniDemo {
  private db!: LSM;
  private store!: KVStore;
  private host: HTMLElement;
  private spec: DemoSpec;
  private els: Record<string, HTMLElement> = {};
  private busy = false;
  private lastTrace: Trace | null = null;
  private results = new Map<string, StrategyResult>();

  constructor(host: HTMLElement, spec: DemoSpec) {
    this.host = host;
    this.spec = spec;
    this.build();
    void this.reset();
  }

  /** Pause so a learner can watch a step land. */
  async pause(ms = 420): Promise<void> {
    await new Promise((r) => setTimeout(r, ms));
  }

  /** Say what the demo is doing right now, above the panels. */
  say(msg: string): void {
    if (this.els.status) this.els.status.textContent = msg;
  }

  private build(): void {
    this.host.classList.add("demo");
    const controls = div("demo-controls");
    for (const b of this.spec.buttons) {
      const btn = document.createElement("button");
      btn.textContent = b.label;
      if (b.title) btn.title = b.title;
      btn.onclick = async () => {
        if (this.busy) return;
        this.busy = true;
        this.setDisabled(true);
        try {
          await b.run(this.db, this);
        } catch (err) {
          this.say("error: " + String(err));
          console.error(err);
        }
        this.busy = false;
        this.setDisabled(false);
        this.render();
      };
      controls.appendChild(btn);
    }
    const reset = document.createElement("button");
    reset.textContent = "reset";
    reset.className = "demo-reset";
    reset.onclick = () => void this.reset();
    controls.appendChild(reset);
    this.host.appendChild(controls);

    this.els.status = div("demo-status");
    this.host.appendChild(this.els.status);

    const stage = div("demo-stage");
    for (const p of this.spec.panels) {
      const row = div(`demo-row demo-row-${p}`);
      const label = div("demo-label");
      label.textContent =
        p === "wal" ? "WAL" : p === "levels" ? "on disk" : p;
      const body = div("demo-body");
      this.els[p] = body;
      row.append(label, body);
      stage.appendChild(row);
    }
    this.host.appendChild(stage);

    this.els.compare = div("demo-compare");
    this.host.appendChild(this.els.compare);

    if (this.spec.note) {
      const note = div("demo-note");
      note.textContent = this.spec.note;
      this.host.appendChild(note);
    }
  }

  private setDisabled(on: boolean): void {
    for (const b of this.host.querySelectorAll("button")) b.disabled = on;
  }

  async reset(override: Partial<EngineConfig> = {}): Promise<void> {
    this.store = new MemoryStore();
    this.db = await LSM.open(this.store, {
      ...TINY,
      ...this.spec.config,
      ...override,
    });
    this.lastTrace = null;
    this.db.events.on((e) => {
      if (e.type === "trace") this.lastTrace = e.trace;
    });
    for (const [k, v] of this.spec.preload ?? []) await this.db.put(k, v);
    await this.db.quiesce();
    this.say("");
    this.render();
  }

  /**
   * Simulate a process kill: drop every scrap of in-memory state and reopen
   * against the same store. Recovery is whatever the WAL can replay — this
   * does not stash the memtable anywhere.
   */
  async crashRecover(): Promise<void> {
    this.db = await LSM.open(this.store, this.db.config);
    this.db.events.on((e) => {
      if (e.type === "trace") this.lastTrace = e.trace;
    });
    this.render();
  }

  /**
   * Run one identical seeded workload under a given compaction strategy and
   * record the resulting amplification numbers, so the two policies can be
   * compared on exactly the same operations.
   */
  async runStrategy(strategy: "leveled" | "tiered"): Promise<void> {
    this.say(`running the workload under ${strategy} compaction…`);
    // The tree has to get genuinely deep before the two policies diverge: at
    // toy scale they do nearly the same work and the comparison shows
    // nothing. These numbers are tuned to reach four levels in a couple of
    // seconds.
    await this.reset({
      compaction: strategy,
      memtableBytes: 512,
      l0Trigger: 4,
      numLevels: 4,
      fanout: 4,
      levelBaseBytes: 2048,
      tieredRunTrigger: 4,
      sparseEvery: 16,
    });
    const w = new Workload(7, { keySpace: 250, valueLen: 16 });
    const WRITES = 1500;
    for (let i = 0; i < WRITES; i++) {
      const op = w.randomPut();
      await this.db.put(op.key, (op as { value: string }).value);
      if (i % 300 === 0) {
        this.say(`${strategy}: ${i}/${WRITES} writes…`);
        await new Promise((r) => setTimeout(r, 0));
      }
    }
    await this.db.flushNow();
    await this.db.quiesce();

    const m = this.db.metrics;
    const before = { gets: m.gets, probed: m.tablesProbed };
    this.say(`${strategy}: measuring reads…`);
    for (let i = 0; i < 200; i++) {
      const op = w.zipfianGet();
      await this.db.get(op.key);
    }
    const gets = m.gets - before.gets;
    const live = await this.db.measureLiveBytes();
    const snap = this.db.snapshot();
    this.results.set(strategy, {
      wa: (m.flushBytesWritten + m.compactionBytesWritten) / m.userBytesWritten,
      ra: gets ? (m.tablesProbed - before.probed) / gets : 0,
      sa: live.bytes ? this.db.totalTableBytes() / live.bytes : 0,
      files: snap.levels.reduce((s, l) => s + l.length, 0),
      compactions: m.compactions,
    });
    this.say(`${strategy}: done — 1500 writes, 200 reads`);
    this.renderComparison();
  }

  private renderComparison(): void {
    const host = this.els.compare;
    if (!host) return;
    host.textContent = "";
    if (this.results.size === 0) return;
    const table = document.createElement("table");
    table.className = "cmp-table";
    const head = document.createElement("tr");
    head.innerHTML =
      "<th>strategy</th><th>write amp</th><th>read amp</th><th>space amp</th><th>files</th><th>compactions</th>";
    table.appendChild(head);
    for (const [name, r] of this.results) {
      const tr = document.createElement("tr");
      tr.innerHTML =
        `<td>${name}</td><td>${r.wa.toFixed(2)}×</td><td>${r.ra.toFixed(2)}</td>` +
        `<td>${r.sa.toFixed(2)}×</td><td>${r.files}</td><td>${r.compactions}</td>`;
      table.appendChild(tr);
    }
    host.appendChild(table);
    if (this.results.size === 2) {
      const l = this.results.get("leveled")!;
      const t = this.results.get("tiered")!;
      // Report the direction actually measured rather than asserting the
      // textbook story — if a run doesn't reproduce it, say so.
      const parts: string[] = [];
      parts.push(
        l.wa > t.wa
          ? `leveled rewrote ${(l.wa / Math.max(t.wa, 0.01)).toFixed(2)}× as many bytes as tiered — the cost of keeping levels tidy`
          : `write amplification came out similar this run (${l.wa.toFixed(2)}× vs ${t.wa.toFixed(2)}×)`,
      );
      parts.push(
        t.ra > l.ra
          ? `tiered checked ${(t.ra / Math.max(l.ra, 0.01)).toFixed(2)}× as many files per read — the cost of merging lazily`
          : `read amplification came out similar this run (${l.ra.toFixed(2)} vs ${t.ra.toFixed(2)} files per read)`,
      );
      host.appendChild(
        div("cmp-verdict", `Same 1500 writes and 200 reads both times. ${parts.join("; ")}.`),
      );
    }
  }

  get engine(): LSM {
    return this.db;
  }

  /** Repaint every panel from live engine state. */
  render(): void {
    void this.renderAsync();
  }

  private async renderAsync(): Promise<void> {
    const snap = this.db.snapshot();

    if (this.els.memtable) {
      const box = this.els.memtable;
      box.textContent = "";
      const entries = [...this.memtableEntries()];
      if (entries.length === 0) {
        box.appendChild(div("demo-empty", "empty"));
      }
      for (const e of entries) {
        const cell = div(
          "kv" + (e.value === null ? " kv-tomb" : ""),
          e.value === null ? `${e.key} ✗` : `${e.key}=${e.value}`,
        );
        cell.title = `seq ${e.seq}` + (e.value === null ? " (tombstone)" : "");
        box.appendChild(cell);
      }
      const cap = div(
        "demo-cap",
        `${snap.memtable.bytes}/${snap.memtable.limit} B`,
      );
      if (snap.memtable.bytes >= snap.memtable.limit * 0.75) {
        cap.classList.add("cap-hot");
      }
      box.appendChild(cap);
    }

    if (this.els.wal) {
      const n = await this.db.walRecordCount();
      this.els.wal.textContent = "";
      this.els.wal.appendChild(
        div(
          "wal-strip" + (n === 0 ? " demo-empty" : ""),
          n === 0
            ? "empty — every write so far is safely in a file"
            : `${n} record${n === 1 ? "" : "s"} not yet in an SSTable`,
        ),
      );
    }

    if (this.els.levels) {
      const box = this.els.levels;
      box.textContent = "";
      let any = false;
      for (let i = 0; i < snap.levels.length; i++) {
        const tables = snap.levels[i];
        if (tables.length === 0 && i > 0) continue;
        any = any || tables.length > 0;
        const row = div("lvl");
        row.appendChild(div("lvl-name", "L" + i));
        if (tables.length === 0) {
          row.appendChild(div("demo-empty", "empty"));
        }
        for (const t of tables) {
          row.appendChild(await this.tableEl(t));
        }
        box.appendChild(row);
      }
      if (!any) {
        box.textContent = "";
        box.appendChild(div("demo-empty", "no files on disk yet"));
      }
    }

    if (this.els.trace) {
      const box = this.els.trace;
      box.textContent = "";
      if (this.lastTrace) {
        const ol = document.createElement("ol");
        ol.className = "trace-steps demo-trace";
        renderTraceSteps(ol, this.lastTrace);
        box.appendChild(ol);
        box.appendChild(div("demo-trace-summary", "⇒ " + this.lastTrace.summary));
      } else {
        box.appendChild(div("demo-empty", "run something to see its steps"));
      }
    }

    if (this.els.metrics) {
      const m = this.db.metrics;
      const physical = m.flushBytesWritten + m.compactionBytesWritten;
      const wa = m.userBytesWritten ? physical / m.userBytesWritten : 0;
      this.els.metrics.textContent = "";
      this.els.metrics.appendChild(
        div(
          "demo-metrics",
          `wrote ${m.userBytesWritten} B of data · engine wrote ${physical} B to disk` +
            (wa ? ` · write amplification ${wa.toFixed(2)}×` : "") +
            ` · ${m.flushes} flush${m.flushes === 1 ? "" : "es"} · ${m.compactions} compaction${m.compactions === 1 ? "" : "s"}`,
        ),
      );
    }
  }

  private *memtableEntries() {
    // snapshot() exposes sizes; for the tiny demo we want the actual keys, so
    // scan the memtable through the engine's own range iterator.
    yield* this.db.memtableSnapshot();
  }

  private async tableEl(t: TableMeta): Promise<HTMLElement> {
    const el = div("demo-sst");
    el.appendChild(div("sst-id", `sst#${t.id}`));
    if (this.spec.expandTables) {
      const reader = await this.db.tableReader(t.id);
      const keys = div("sst-keys");
      for (const e of reader.iterate()) {
        keys.appendChild(
          div(
            "kv kv-sm" + (e.value === null ? " kv-tomb" : ""),
            e.value === null ? `${e.key} ✗` : `${e.key}=${e.value}`,
          ),
        );
      }
      el.appendChild(keys);
    } else {
      el.appendChild(div("sst-range", `${t.minKey}…${t.maxKey}`));
    }
    el.appendChild(div("sst-meta", `${t.size} B · ${t.entryCount} entries`));
    return el;
  }
}

function div(cls: string, text?: string): HTMLElement {
  const d = document.createElement("div");
  d.className = cls;
  if (text !== undefined) d.textContent = text;
  return d;
}
